import {ChangeDetectionStrategy, Component, computed, inject, signal} from '@angular/core';
import {BzDialogRef} from '@bringoz/ui';
import * as moment from 'moment-timezone';
import {injectLegacy} from '../../../../commons/angular/utils/inject-legacy.util';
import type {SplitService} from '../../../../commons/services/split-service.service';
import {MondelezSummaryDialogResult} from '../../../types/timekeeping-dialog.type';

const DAY_FORMAT = 'YYYY-MM-DD';

/**
 * Picker value (local midnight) of the calendar day that `momentInZone` falls on. The date pickers show
 * timestamps in the browser's zone, so the days are kept as local midnights and only turned into the site's
 * zone when the dialog closes.
 */
function toPickerDay(momentInZone: moment.Moment): number {
  return moment(momentInZone.format(DAY_FORMAT), DAY_FORMAT).valueOf();
}

/**
 * Picks the date range of the Mondelez Summary Report. Opens on the previous Sunday-Saturday week.
 * Two date fields instead of the range picker: that one opens its own dialog, which stays under this one.
 */
require('../timekeeping-dialogs.scss');
@Component({
  standalone: false,
  selector: 'app-timekeeping-mondelez-summary-dialog',
  templateUrl: './timekeeping-mondelez-summary-dialog.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TimekeepingMondelezSummaryDialogComponent {

  private readonly ref = inject<BzDialogRef<MondelezSummaryDialogResult>>(BzDialogRef);
  private readonly zoneId = injectLegacy<SplitService>('splitService').getTimeZone();

  readonly from = signal<number | null>(null);
  readonly to = signal<number | null>(null);
  readonly invalid = computed(() => !this.from() || !this.to() || this.from() > this.to());

  constructor() {
    this.selectPreviousWeek();
  }

  selectCalendarWeek() {
    this.selectSundayToSaturday(0);
  }

  selectPreviousWeek() {
    this.selectSundayToSaturday(1);
  }

  onFromChange(timestamp: number) {
    this.from.set(timestamp || null);
  }

  onToChange(timestamp: number) {
    this.to.set(timestamp || null);
  }

  onCancel() {
    this.ref.close();
  }

  onGenerate() {
    this.ref.close({
      from: this.startOfDayInZone(this.from()),
      to: this.endOfDayInZone(this.to()),
      zoneId: this.zoneId
    });
  }

  /**
   * Sunday-Saturday week, `weeksAgo` weeks back, counted from today in the site's zone. Built from the weekday
   * number instead of startOf('week'), which follows the locale and would start on Monday for some sites.
   */
  private selectSundayToSaturday(weeksAgo: number) {
    const offset = -7 * weeksAgo;
    this.from.set(toPickerDay(moment.tz(this.zoneId).day(offset)));
    this.to.set(toPickerDay(moment.tz(this.zoneId).day(offset + 6)));
  }

  private startOfDayInZone(pickerDay: number): number {
    return moment.tz(moment(pickerDay).format(DAY_FORMAT), this.zoneId).startOf('day').valueOf();
  }

  private endOfDayInZone(pickerDay: number): number {
    return moment.tz(moment(pickerDay).format(DAY_FORMAT), this.zoneId).endOf('day').valueOf();
  }
}
